import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Share2, Loader2, Sparkles, Copy, Trash2, Download, ExternalLink, Send, CheckCircle2, AlertTriangle, Settings, Film, Play, Eye, Clock, Calendar, X as XIcon } from 'lucide-react';
import { SocialRewriteDialog } from '@/components/admin/SocialRewriteDialog';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';
import RefImageLibrary from '@/components/admin/RefImageLibrary';
import CrossPostPreviewDialog from './CrossPostPreviewDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const FORMATS = [
  { value: 'square', label: 'Square 1080×1080 (IG / FB / LinkedIn feed)' },
  { value: 'story', label: 'Story 1080×1920 (IG / FB story)' },
  { value: 'landscape', label: 'Landscape 1200×630 (FB / LinkedIn / YouTube thumbnail)' },
  { value: 'reel', label: 'Reel 1080×1920 video (IG / TikTok / YouTube Shorts)' },
];
const PLATFORMS = ['Facebook', 'Instagram', 'LinkedIn', 'TikTok', 'Threads', 'YouTube'];

const REEL_SCENES = [3, 4, 5, 6];

const SocialMediaGeneratorTab = () => {
  const [prompt, setPrompt] = useState('');
  // Free-form image prompt — overrides the auto image prompt (which is
  // currently the caption prompt itself). Lets the admin describe what
  // the social media image should look like, separate from the post topic.
  const [imagePrompt, setImagePrompt] = useState('');
  // Reference-library images (GridFS ids) attached to image generation.
  const [refImageIds, setRefImageIds] = useState([]);
  // "Rewrite with AI" dialog state — opens from the sparkles button on
  // each draft row. Non-destructive; only persists on Apply.
  const [rewriteOpenDraft, setRewriteOpenDraft] = useState(null);
  const [platform, setPlatform] = useState('Instagram');
  const [format, setFormat] = useState('square');
  const [tone, setTone] = useState('');
  const [includeHashtags, setIncludeHashtags] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [reelScenes, setReelScenes] = useState(5);
  const [bgTrack, setBgTrack] = useState('upbeat');
  const [bgVolume, setBgVolume] = useState(0.35);
  const [audioTracks, setAudioTracks] = useState([]);
  const audioUploadRef = React.useRef(null);
  const [metaConfig, setMetaConfig] = useState(null);
  const [showMetaSetup, setShowMetaSetup] = useState(false);
  const [metaForm, setMetaForm] = useState({ page_id: '', page_access_token: '', ig_user_id: '' });
  const [threadsToken, setThreadsToken] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);
  const [savingThreads, setSavingThreads] = useState(false);
  const [publishingDraftId, setPublishingDraftId] = useState(null);
  const [previewDraft, setPreviewDraft] = useState(null);
  const [scheduled, setScheduled] = useState([]);
  const [schedulingDraftId, setSchedulingDraftId] = useState(null);
  const [scheduleAt, setScheduleAt] = useState('');
  const [schedulePlatforms, setSchedulePlatforms] = useState({
    Facebook: true, Instagram: true, LinkedIn: false, TikTok: false, Threads: true, YouTube: false,
  });
  const [scheduleBusy, setScheduleBusy] = useState(false);

  const loadScheduled = useCallback(async () => {
    const { data } = await apiFetch('/api/admin/social/scheduled');
    setScheduled(data?.items || []);
  }, []);

  // Auto blog → social — config + run-now plumbing
  const [b2sConfig, setB2sConfig] = useState(null);
  const [b2sBusy, setB2sBusy] = useState(false);
  const [b2sRunBusy, setB2sRunBusy] = useState(false);

  const loadB2sConfig = useCallback(async () => {
    const { data } = await apiFetch('/api/admin/social/blog-to-social/config');
    if (data) setB2sConfig(data);
  }, []);

  const saveB2sConfig = async (patch) => {
    if (!b2sConfig) return;
    const next = { ...b2sConfig, ...patch };
    setB2sConfig(next);  // optimistic
    setB2sBusy(true);
    try {
      const { error } = await apiFetch('/api/admin/social/blog-to-social/config', {
        method: 'POST',
        body: {
          enabled: next.enabled,
          platforms: next.platforms,
          format: next.format,
          auto_schedule: next.auto_schedule,
          schedule_offset_hours: next.schedule_offset_hours,
        },
      });
      if (error) {
        toast.error(error);
        loadB2sConfig(); // rollback
        return;
      }
      toast.success(next.enabled ? 'Auto-post enabled — new blog posts will be processed.' : 'Auto-post disabled.');
    } finally {
      setB2sBusy(false);
    }
  };

  const runB2sNow = async () => {
    setB2sRunBusy(true);
    try {
      const { data, error } = await apiFetch('/api/admin/social/blog-to-social/run-now', {
        method: 'POST',
        body: {},
      });
      if (error) return toast.error(error || 'Run failed');
      if (data?.skipped) {
        toast.info(`Skipped: ${data.skipped}`);
      } else if (data?.generated > 0) {
        toast.success(`Generated ${data.generated} draft(s) from blog posts.`);
        loadDrafts();
        loadScheduled();
      } else {
        toast.info(`No new blog posts to convert (${data?.checked || 0} checked).`);
      }
      loadB2sConfig();
    } finally {
      setB2sRunBusy(false);
    }
  };

  const toggleB2sPlatform = (platform) => {
    if (!b2sConfig) return;
    const current = new Set(b2sConfig.platforms);
    if (current.has(platform)) {
      if (current.size === 1) {
        toast.error('At least one platform is required');
        return;
      }
      current.delete(platform);
    } else {
      current.add(platform);
    }
    saveB2sConfig({ platforms: Array.from(current) });
  };

  // Best-time-to-post hints — sourced from each platform's published
  // engagement data (the same numbers we surface in the
  // manual-platforms-instructions card). Times are stated in the
  // admin's LOCAL TZ for the time-of-day component, and use a *next-
  // occurrence-of-this-weekday* lookup for the weekday component.
  // When multiple platforms are selected we average their times so
  // a single timestamp lands in the best joint window.
  //
  // (Treat as guidance — real analytics will eventually replace this.)
  const PLATFORM_BEST_TIMES = {
    // [weekday 0=Sun…6=Sat, hour 24h]
    Facebook:  { weekday: 3, hour: 13 },   // Wed 1 PM
    Instagram: { weekday: 3, hour: 11 },   // Wed 11 AM
    LinkedIn:  { weekday: 2, hour: 9 },    // Tue 9 AM
    TikTok:    { weekday: 3, hour: 19 },   // Wed 7 PM
    Threads:   { weekday: 3, hour: 12 },   // Wed noon
    YouTube:   { weekday: 0, hour: 10 },   // Sun 10 AM
  };

  /** Compute the next-occurrence Date for the supplied weekday + hour,
      respecting the admin's local timezone. Always returns a Date in
      the FUTURE (skips to next week if today's slot already passed). */
  const nextOccurrence = (weekday, hour) => {
    const now = new Date();
    const d = new Date(now);
    d.setSeconds(0); d.setMilliseconds(0); d.setMinutes(0);
    d.setHours(hour);
    const diff = (weekday - d.getDay() + 7) % 7;
    d.setDate(d.getDate() + diff);
    if (d.getTime() <= now.getTime()) d.setDate(d.getDate() + 7);
    return d;
  };

  /** Suggest a single timestamp + label for the currently-checked
      platforms. Returns null when nothing selected. */
  const suggestBestTime = (platformsMap) => {
    const selected = Object.entries(platformsMap)
      .filter(([_, v]) => v)
      .map(([k]) => k);
    if (selected.length === 0) return null;
    if (selected.length === 1) {
      const cfg = PLATFORM_BEST_TIMES[selected[0]];
      const when = nextOccurrence(cfg.weekday, cfg.hour);
      const dayName = when.toLocaleDateString(undefined, { weekday: 'long' });
      const timeName = when.toLocaleTimeString(undefined, { hour: 'numeric' });
      return { date: when, label: `Optimal for ${selected[0]} — ${dayName} ${timeName}` };
    }
    // Multiple platforms — find the candidate slot that's soonest
    // among each platform's next-occurrence. This favours getting
    // posted quickly while still respecting at least one platform's
    // peak. (Picking soonest is more useful than averaging because
    // an averaged time may not actually be peak for any platform.)
    let bestSlot = null;
    let bestPlatform = null;
    for (const p of selected) {
      const cfg = PLATFORM_BEST_TIMES[p];
      const cand = nextOccurrence(cfg.weekday, cfg.hour);
      if (!bestSlot || cand.getTime() < bestSlot.getTime()) {
        bestSlot = cand;
        bestPlatform = p;
      }
    }
    const dayName = bestSlot.toLocaleDateString(undefined, { weekday: 'long' });
    const timeName = bestSlot.toLocaleTimeString(undefined, { hour: 'numeric' });
    return {
      date: bestSlot,
      label: `Soonest optimal — ${bestPlatform} ${dayName} ${timeName}`,
    };
  };

  /** Click handler: apply the current suggestion to the schedule input. */
  const applySuggestedTime = () => {
    const s = suggestBestTime(schedulePlatforms);
    if (!s) {
      toast.error('Pick at least one platform first');
      return;
    }
    const pad = (n) => String(n).padStart(2, '0');
    const d = s.date;
    const localStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setScheduleAt(localStr);
    toast.success(s.label);
  };

  // Open the schedule modal for a draft. Default to "1 hour from now"
  // rounded to the next quarter hour so the admin lands on a sensible
  // suggestion they can quickly adjust.
  const openSchedule = (draft) => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15);
    now.setSeconds(0);
    now.setMilliseconds(0);
    // datetime-local needs local-time string without TZ suffix
    const pad = (n) => String(n).padStart(2, '0');
    const localStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    setScheduleAt(localStr);
    setSchedulingDraftId(draft.draft_id);
  };

  const confirmSchedule = async () => {
    if (!schedulingDraftId || !scheduleAt) return;
    const chosen = Object.entries(schedulePlatforms).filter(([_, v]) => v).map(([k]) => k);
    if (chosen.length === 0) return toast.error('Pick at least one platform');
    const utcIso = new Date(scheduleAt).toISOString();
    setScheduleBusy(true);
    try {
      const { error } = await apiFetch(`/api/admin/social/drafts/${schedulingDraftId}/schedule`, {
        method: 'POST',
        body: { scheduled_at: utcIso, platforms: chosen },
      });
      if (error) return toast.error(error || 'Schedule failed');
      toast.success(`Scheduled for ${new Date(utcIso).toLocaleString()}`);
      setSchedulingDraftId(null);
      loadDrafts();
      loadScheduled();
    } finally {
      setScheduleBusy(false);
    }
  };

  const cancelSchedule = async (draftId) => {
    if (!window.confirm('Cancel this scheduled post?')) return;
    const { error } = await apiFetch(`/api/admin/social/drafts/${draftId}/schedule`, { method: 'DELETE' });
    if (error) return toast.error(error || 'Could not cancel');
    toast.success('Schedule cancelled');
    loadDrafts();
    loadScheduled();
  };

  const loadDrafts = useCallback(async () => {
    setLoadingDrafts(true);
    const { data } = await apiFetch('/api/admin/social/drafts');
    setLoadingDrafts(false);
    setDrafts(data || []);
  }, []);

  const loadMetaConfig = useCallback(async () => {
    const { data } = await apiFetch('/api/admin/meta/config');
    setMetaConfig(data || null);
  }, []);

  const loadAudioTracks = useCallback(async () => {
    const { data } = await apiFetch('/api/admin/reels/audio');
    setAudioTracks(data || []);
  }, []);

  useEffect(() => { loadDrafts(); loadMetaConfig(); loadAudioTracks(); loadScheduled(); loadB2sConfig(); }, [loadDrafts, loadMetaConfig, loadAudioTracks, loadScheduled, loadB2sConfig]);

  const uploadAudio = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error('Max 10 MB');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/admin/reels/audio/upload`, {
        method: 'POST', body: fd, credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Upload failed');
      toast.success(`Uploaded "${data.label}"`);
      loadAudioTracks();
      setBgTrack(data.id);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const deleteCustomTrack = async (trackId) => {
    if (!window.confirm('Delete this custom track?')) return;
    const { error } = await apiFetch(`/api/admin/reels/audio/${encodeURIComponent(trackId)}`, { method: 'DELETE' });
    if (error) return toast.error(error);
    toast.success('Deleted');
    if (bgTrack === trackId) setBgTrack('upbeat');
    loadAudioTracks();
  };

  const saveMetaConfig = async () => {
    if (!metaForm.page_id.trim() || !metaForm.page_access_token.trim()) {
      return toast.error('Page ID and Access Token are required');
    }
    setSavingMeta(true);
    const { data, error } = await apiFetch('/api/admin/meta/config', {
      method: 'POST',
      body: {
        page_id: metaForm.page_id.trim(),
        page_access_token: metaForm.page_access_token.trim(),
        ig_user_id: metaForm.ig_user_id.trim() || null,
      },
    });
    setSavingMeta(false);
    if (error) return toast.error(error);
    toast.success(`Connected: ${data.fb_page_name}`);
    setShowMetaSetup(false);
    setMetaForm({ page_id: '', page_access_token: '', ig_user_id: '' });
    loadMetaConfig();
  };

  const disconnectMeta = async () => {
    if (!window.confirm('Disconnect Meta? You will need to re-enter your token to publish.')) return;
    await apiFetch('/api/admin/meta/config', { method: 'DELETE' });
    toast.success('Disconnected');
    loadMetaConfig();
  };

  const saveThreads = async () => {
    if (!threadsToken.trim()) return toast.error('Threads access token required');
    setSavingThreads(true);
    const { data, error } = await apiFetch('/api/admin/meta/threads-config', {
      method: 'POST',
      body: { threads_access_token: threadsToken.trim() },
    });
    setSavingThreads(false);
    if (error) return toast.error(error);
    toast.success(`Threads connected: @${data.threads_username}`);
    setThreadsToken('');
    loadMetaConfig();
  };

  const disconnectThreads = async () => {
    if (!window.confirm('Disconnect Threads?')) return;
    await apiFetch('/api/admin/meta/threads-config', { method: 'DELETE' });
    toast.success('Threads disconnected');
    loadMetaConfig();
  };

  const publishToMeta = async (draftId, publishFb, publishIg, publishThreads) => {
    if (!metaConfig?.configured && !metaConfig?.threads_configured) return toast.error('Connect Meta or Threads first');
    setPublishingDraftId(draftId);
    const { data, error } = await apiFetch('/api/admin/meta/publish', {
      method: 'POST',
      body: {
        draft_id: draftId,
        publish_to_facebook: publishFb,
        publish_to_instagram: publishIg,
        publish_to_threads: publishThreads,
      },
    });
    setPublishingDraftId(null);
    if (error) return toast.error(error);
    const ok = [];
    if (data.facebook) ok.push('Facebook');
    if (data.instagram) ok.push('Instagram');
    if (data.threads) ok.push('Threads');
    if (ok.length) toast.success(`Published to ${ok.join(' + ')}`);
    if (data.errors?.length) data.errors.forEach((e) => toast.error(e));
    loadDrafts();
  };

  const generate = async () => {
    if (prompt.trim().length < 8) return toast.error('Prompt too short');
    setGenerating(true);

    const isReel = format === 'reel';
    const endpoint = isReel ? '/api/admin/reels/generate' : '/api/admin/social/generate';
    const body = isReel
      ? {
          prompt: prompt.trim(),
          platform,
          tone: tone || null,
          include_hashtags: includeHashtags,
          scenes: reelScenes,
          aspect: '9:16',
          bg_track: bgTrack || null,
          bg_volume: bgVolume,
        }
      : {
          prompt: prompt.trim(),
          platform,
          format,
          tone: tone || null,
          include_hashtags: includeHashtags,
          image_prompt: imagePrompt.trim() || null,
          reference_image_ids: refImageIds,
        };

    const { data, error } = await apiFetch(endpoint, { method: 'POST', body });
    setGenerating(false);
    if (error) return toast.error(error);
    toast.success(isReel ? `Reel ready (${data.duration_seconds?.toFixed(1)}s)` : `Generated ${data.format_label}`);
    // Refresh draft list so reel record (with video_url) shows up
    loadDrafts();
  };

  const copyCaption = (text, hashtags) => {
    const full = hashtags?.length ? `${text}\n\n${hashtags.map((h) => `#${h}`).join(' ')}` : text;
    navigator.clipboard.writeText(full).then(
      () => toast.success('Caption + hashtags copied'),
      () => toast.error('Copy failed'),
    );
  };

  const deleteDraft = async (id) => {
    if (!window.confirm('Delete this draft?')) return;
    const { error } = await apiFetch(`/api/admin/social/drafts/${id}`, { method: 'DELETE' });
    if (error) return toast.error(error);
    toast.success('Deleted');
    setDrafts((d) => d.filter((x) => x.draft_id !== id));
  };

  return (
    <div className="space-y-4" data-testid="social-gen-tab">
      {/* Meta Direct-Publish Connection Status */}
      <Card className={metaConfig?.configured ? 'border-emerald-200' : 'border-amber-200'}>
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            {metaConfig?.configured ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-medium">Connected to {metaConfig.fb_page_name || 'Meta'}</p>
                  <p className="text-xs text-slate-500">
                    Page {metaConfig.page_id} · IG {metaConfig.ig_user_id || 'not linked'} · Token …{metaConfig.token_tail}
                  </p>
                </div>
              </>
            ) : (
              <>
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="text-sm font-medium">Meta not connected</p>
                  <p className="text-xs text-slate-500">Connect to publish directly to Facebook + Instagram.</p>
                </div>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowMetaSetup((v) => !v)} data-testid="meta-toggle-setup">
              <Settings className="h-3 w-3 mr-1" /> {metaConfig?.configured ? 'Update' : 'Connect'}
            </Button>
            {metaConfig?.configured && (
              <Button size="sm" variant="ghost" className="text-red-600" onClick={disconnectMeta} data-testid="meta-disconnect">
                Disconnect
              </Button>
            )}
          </div>
        </CardContent>
        {showMetaSetup && (
          <CardContent className="p-4 pt-0 space-y-3 border-t">
            <div className="text-xs text-slate-600 bg-amber-50 border-l-4 border-amber-400 p-3 rounded">
              <p className="font-medium mb-1">Facebook + Instagram setup:</p>
              <ol className="list-decimal pl-4 space-y-1">
                <li>In Meta Business Suite → <strong>Pages → Clear Communication App → Page Roles</strong> → add your personal Facebook account as a <strong>Page Admin</strong> (required for Graph API access).</li>
                <li>Go to <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer" className="underline">Graph API Explorer</a> → change the <strong>"User or Page"</strong> dropdown from "User Token" to <strong>"Clear Communication App"</strong> → "Generate Access Token" with scopes: <code className="bg-white px-1 rounded">pages_read_engagement, pages_manage_posts, instagram_basic, instagram_content_publish</code>.</li>
                <li>Run <code className="bg-white dark:bg-slate-800 dark:text-slate-100 px-1 rounded">GET /me</code> → copy the Page <code>id</code>. The token in the explorer is now the Page Access Token.</li>
                <li>Paste both below. We'll auto-detect your linked Instagram Business account.</li>
                <li>⚠️ Long-lived tokens last ~60 days — we'll email you 7 days before expiry.</li>
              </ol>
            </div>
            <div>
              <Label htmlFor="meta-page-id" className="text-xs">Facebook Page ID (for Clear Communication App)</Label>
              <Input
                id="meta-page-id"
                value={metaForm.page_id}
                onChange={(e) => setMetaForm((f) => ({ ...f, page_id: e.target.value }))}
                placeholder="e.g. 1491260965838600"
                data-testid="meta-page-id-input"
              />
            </div>
            <div>
              <Label htmlFor="meta-token" className="text-xs">Page Access Token (long-lived)</Label>
              <Textarea
                id="meta-token"
                rows={2}
                className="font-mono text-xs"
                value={metaForm.page_access_token}
                onChange={(e) => setMetaForm((f) => ({ ...f, page_access_token: e.target.value }))}
                placeholder="EAAGm0..."
                data-testid="meta-token-input"
              />
            </div>
            <div>
              <Label htmlFor="meta-ig-id" className="text-xs">IG Business Account ID (optional — auto-detected)</Label>
              <Input
                id="meta-ig-id"
                value={metaForm.ig_user_id}
                onChange={(e) => setMetaForm((f) => ({ ...f, ig_user_id: e.target.value }))}
                placeholder="17841405822304914"
                data-testid="meta-ig-id-input"
              />
            </div>
            <Button onClick={saveMetaConfig} disabled={savingMeta} className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00d4bd]" data-testid="meta-save-btn">
              {savingMeta ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
              Validate & save FB+IG
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Threads connection card */}
      <Card className={metaConfig?.threads_configured ? 'border-emerald-200' : 'border-slate-200'}>
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            {metaConfig?.threads_configured ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-medium">Threads connected</p>
                  <p className="text-xs text-slate-500">User ID {metaConfig.threads_user_id} · Token …{metaConfig.threads_token_tail}</p>
                </div>
              </>
            ) : (
              <>
                <AlertTriangle className="h-5 w-5 text-slate-400" />
                <div>
                  <p className="text-sm font-medium">Threads not connected</p>
                  <p className="text-xs text-slate-500">Optional — connect to also publish to @threads.</p>
                </div>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setThreadsToken(threadsToken ? '' : ' ')} data-testid="threads-toggle">
              <Settings className="h-3 w-3 mr-1" /> {metaConfig?.threads_configured ? 'Update' : 'Connect'} Threads
            </Button>
            {metaConfig?.threads_configured && (
              <Button size="sm" variant="ghost" className="text-red-600" onClick={disconnectThreads} data-testid="threads-disconnect">
                Disconnect
              </Button>
            )}
          </div>
        </CardContent>
        {threadsToken !== '' && (
          <CardContent className="p-4 pt-0 space-y-3 border-t">
            <div className="text-xs text-slate-600 bg-amber-50 border-l-4 border-amber-400 p-3 rounded">
              <p className="font-medium mb-1">Threads setup (separate from FB token):</p>
              <ol className="list-decimal pl-4 space-y-1">
                <li>Go to <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="underline">developers.facebook.com/apps</a> → Use Cases → <strong>"Access the Threads API"</strong>.</li>
                <li>Add the Threads API product. Configure scopes: <code className="bg-white dark:bg-slate-800 dark:text-slate-100 px-1 rounded">threads_basic, threads_content_publish</code>.</li>
                <li>In <strong>Roles</strong>, add your Threads account as a Threads Tester. Accept the invite from the Threads app.</li>
                <li>Use Threads OAuth at <code className="bg-white dark:bg-slate-800 dark:text-slate-100 px-1 rounded text-[10px]">https://www.threads.net/oauth/authorize</code> to generate a token. Or use the API Setup tool in the app dashboard for a quick long-lived token.</li>
                <li>Paste the long-lived Threads token below. We'll fetch your Threads user ID automatically.</li>
              </ol>
            </div>
            <div>
              <Label htmlFor="threads-token" className="text-xs">Threads Access Token (long-lived)</Label>
              <Textarea
                id="threads-token"
                rows={2}
                className="font-mono text-xs"
                value={threadsToken.trim()}
                onChange={(e) => setThreadsToken(e.target.value)}
                placeholder="THAAxxxx..."
                data-testid="threads-token-input"
              />
            </div>
            <Button onClick={saveThreads} disabled={savingThreads || !threadsToken.trim()} className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00d4bd]" data-testid="threads-save-btn">
              {savingThreads ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
              Validate & save Threads
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Manual-publish platforms — LinkedIn, TikTok, YouTube — these
          require user-driven OAuth flows and uploads that aren't worth
          automating until you have publishing cadence to justify it.
          For now, the generator pumps out the asset + caption and you
          paste-and-post manually. */}
      <details
        className="rounded-lg border bg-card"
        data-testid="manual-platforms-instructions"
      >
        <summary className="cursor-pointer px-4 py-3 text-sm font-medium flex items-center gap-2 text-muted-foreground hover:text-foreground">
          <Settings className="h-4 w-4" />
          LinkedIn / TikTok / YouTube — manual posting (linking instructions)
        </summary>
        <div className="p-4 pt-2 space-y-4 border-t text-sm">
          <p className="text-xs text-muted-foreground">
            These platforms don't have a 1-click "Publish from CCA" hook yet (their APIs require app reviews / business verifications that aren't worth the lift until cadence justifies it). Use the generator to produce the asset + caption, then paste-and-post on each platform.
          </p>

          {/* LinkedIn */}
          <div className="rounded-md border-l-4 border-[#0A66C2] bg-[#0A66C2]/5 p-3 space-y-1.5">
            <p className="font-medium text-sm">LinkedIn (Company Page or Personal Profile)</p>
            <div className="rounded-md border bg-card p-2.5 space-y-1.5" data-testid="linkedin-rss-callout">
              <p className="text-xs font-semibold text-[#0A66C2]">
                🔗 Easiest path — auto-publish your blog via LinkedIn's "Newsletter / RSS" feature
              </p>
              <p className="text-xs">Paste this RSS URL into LinkedIn's <strong>Manage RSS</strong> step:</p>
              <div className="flex items-center gap-1.5 bg-muted/40 rounded px-2 py-1">
                <code className="flex-1 text-[11px] font-mono break-all select-all" data-testid="rss-feed-url">
                  https://clearcommunicationapp.com/api/rss/blog.xml
                </code>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    navigator.clipboard.writeText('https://clearcommunicationapp.com/api/rss/blog.xml');
                    toast.success('RSS URL copied');
                  }}
                  className="h-7 px-2"
                  data-testid="copy-rss-feed-url"
                  title="Copy RSS feed URL"
                >
                  <Copy className="h-3 w-3" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  asChild
                  className="h-7 px-2"
                  title="Preview RSS feed"
                >
                  <a href="https://clearcommunicationapp.com/api/rss/blog.xml" target="_blank" rel="noreferrer" data-testid="open-rss-feed">
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground">
                Updates within 5 minutes of each blog publish. Same feed works for Feedly, Inoreader, Substack-imports, etc.
              </p>
            </div>
            <p className="text-xs font-semibold mt-2">…or post manually</p>
            <ol className="list-decimal pl-5 text-xs space-y-1">
              <li>Sign in to <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="text-[#0A66C2] underline">linkedin.com</a> as the Page admin (or yourself for personal).</li>
              <li>For Company Page: top nav → <strong>"Me"</strong> dropdown → under "Manage" select <strong>ClearCommunicationApp</strong> page.</li>
              <li>Click <strong>Start a post</strong> → upload the generated image (square or landscape recommended).</li>
              <li>Paste the caption + hashtags from this tab → <strong>Post</strong>.</li>
              <li>For analytics: Page → <strong>Analytics → Content</strong> tab.</li>
            </ol>
            <p className="text-[11px] text-muted-foreground italic">
              Best format: <code>landscape 1200×630</code>. Best post times: Tues–Thurs 8–10 AM ET.
            </p>
          </div>

          {/* TikTok */}
          <div className="rounded-md border-l-4 border-black dark:border-white bg-black/5 dark:bg-white/5 p-3 space-y-1.5">
            <p className="font-medium text-sm">TikTok (Business Account)</p>
            <ol className="list-decimal pl-5 text-xs space-y-1">
              <li>Open the <strong>TikTok mobile app</strong> (uploads from desktop are still flaky).</li>
              <li>Sign in to your business account → tap the <strong>+</strong> button at the bottom.</li>
              <li>Tap <strong>Upload</strong> → choose the reel MP4 generated in this tab.</li>
              <li>Tap <strong>Next</strong> → paste caption + hashtags → toggle <strong>"Allow comments / Duet / Stitch"</strong> as desired → <strong>Post</strong>.</li>
              <li>To switch to Business account (if not done): Profile → ☰ → Settings → Account → <strong>Switch to Business Account</strong>. Needed for analytics + commercial-music license.</li>
            </ol>
            <p className="text-[11px] text-muted-foreground italic">
              Best format: <code>reel 1080×1920</code>, 7–15 s, music: use TikTok's Commercial Sounds library to stay compliant. Best post times: Tues–Thurs 6–10 PM local.
            </p>
          </div>

          {/* YouTube */}
          <div className="rounded-md border-l-4 border-red-600 bg-red-500/5 p-3 space-y-1.5">
            <p className="font-medium text-sm">YouTube (Shorts + thumbnails)</p>
            <ol className="list-decimal pl-5 text-xs space-y-1">
              <li>Sign in to <a href="https://studio.youtube.com" target="_blank" rel="noreferrer" className="text-red-600 underline">studio.youtube.com</a> as the channel owner.</li>
              <li>Top-right <strong>Create</strong> → <strong>Upload video</strong> (Shorts: any vertical video ≤60 s is auto-classified).</li>
              <li>Upload the reel MP4 (1080×1920) → fill in title from caption first line → description = full caption + hashtags.</li>
              <li>For regular videos: upload the <code>landscape 1200×630</code> as the thumbnail under <strong>Video elements → Thumbnail → Upload</strong>.</li>
              <li>Set <strong>Audience</strong>: "No, it's not made for kids" (required) → <strong>Publish</strong>.</li>
            </ol>
            <p className="text-[11px] text-muted-foreground italic">
              Best format for Shorts: <code>reel 1080×1920</code> ≤60 s. Best for thumbnails: <code>landscape 1200×630</code>, high-contrast text + face for CTR. Best post times: Sat–Sun 9 AM–12 PM local.
            </p>
          </div>

          <p className="text-[11px] text-muted-foreground">
            Future automation: LinkedIn UGC API, TikTok Content Posting API, and YouTube Data API v3 can all be wired up when posting volume justifies it. Ping me and I'll add them.
          </p>
        </div>
      </details>

      {/* Auto Blog → Social — set it and forget it. When enabled, every
          newly-published blog post automatically becomes a draft (caption
          + image via Claude/Nano Banana) and (optionally) lands in the
          schedule queue at the next peak posting slot. */}
      {b2sConfig && (
        <Card
          className={b2sConfig.enabled ? 'border-[#00A896]/50 bg-[#00E5CC]/5' : 'border-dashed'}
          data-testid="auto-b2s-card"
        >
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className={`h-4 w-4 ${b2sConfig.enabled ? 'text-[#00A896]' : 'text-muted-foreground'}`} />
              Auto-post blog to social
              {b2sConfig.enabled && (
                <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
                  Active
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="text-xs">
              Set-and-forget: every published blog post is auto-converted into a social draft + (optionally) scheduled. Cron runs every 5 minutes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 pt-2">
            {/* Master switch */}
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={b2sConfig.enabled}
                  onChange={(e) => saveB2sConfig({ enabled: e.target.checked })}
                  disabled={b2sBusy}
                  className="h-4 w-4 accent-[#00A896]"
                  data-testid="auto-b2s-enabled-toggle"
                />
                <span className="text-sm font-medium">
                  {b2sConfig.enabled ? 'Auto-post is ON' : 'Turn ON auto-post'}
                </span>
              </label>
              {b2sConfig.enabled && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={runB2sNow}
                  disabled={b2sRunBusy}
                  className="h-7 text-xs border-[#00A896]/40 hover:bg-[#00E5CC]/10"
                  data-testid="auto-b2s-run-now-btn"
                  title="Run the converter immediately against any new posts"
                >
                  {b2sRunBusy ? (
                    <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Running…</>
                  ) : (
                    <><Sparkles className="h-3 w-3 mr-1" /> Run now</>
                  )}
                </Button>
              )}
            </div>

            {/* Platform selection */}
            <div>
              <Label className="text-xs">Publish to (in order of priority — first one is the "primary")</Label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mt-1">
                {['Facebook', 'Instagram', 'LinkedIn', 'TikTok', 'Threads', 'YouTube'].map((p) => {
                  const checked = b2sConfig.platforms.includes(p);
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => toggleB2sPlatform(p)}
                      disabled={b2sBusy}
                      className={`px-2 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                        checked
                          ? 'bg-[#00E5CC] text-[#0d1f2d] border-[#00A896]'
                          : 'bg-muted text-muted-foreground hover:border-[#00A896]/40'
                      }`}
                      data-testid={`auto-b2s-platform-${p.toLowerCase()}`}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Format + scheduling controls */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Format</Label>
                <Select
                  value={b2sConfig.format}
                  onValueChange={(v) => saveB2sConfig({ format: v })}
                  disabled={b2sBusy}
                >
                  <SelectTrigger className="h-8" data-testid="auto-b2s-format-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="square">Square 1080×1080</SelectItem>
                    <SelectItem value="landscape">Landscape 1200×630</SelectItem>
                    <SelectItem value="story">Story 1080×1920</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tone of voice</Label>
                <Select
                  value={b2sConfig.tone || 'educational'}
                  onValueChange={(v) => saveB2sConfig({ tone: v })}
                  disabled={b2sBusy}
                >
                  <SelectTrigger className="h-8" data-testid="auto-b2s-tone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="educational">Educational</SelectItem>
                    <SelectItem value="warm-personal">Warm &amp; personal</SelectItem>
                    <SelectItem value="direct">Direct</SelectItem>
                    <SelectItem value="funny">Funny</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Schedule mode</Label>
                <Select
                  value={b2sConfig.auto_schedule ? 'peak' : 'offset'}
                  onValueChange={(v) => saveB2sConfig({ auto_schedule: v === 'peak' })}
                  disabled={b2sBusy}
                >
                  <SelectTrigger className="h-8" data-testid="auto-b2s-schedule-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="peak">Soonest peak slot</SelectItem>
                    <SelectItem value="offset">Fixed offset hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!b2sConfig.auto_schedule && (
              <div>
                <Label className="text-xs">Offset hours after blog publish</Label>
                <Input
                  type="number"
                  min={0}
                  max={168}
                  value={b2sConfig.schedule_offset_hours}
                  onChange={(e) => saveB2sConfig({ schedule_offset_hours: Math.max(0, Math.min(168, parseInt(e.target.value) || 0)) })}
                  className="h-8 max-w-[120px]"
                  data-testid="auto-b2s-offset-hours"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  How long after a blog post is published to schedule the social draft. 0 = publish immediately.
                </p>
              </div>
            )}

            {b2sConfig.last_run_at && (
              <p className="text-[11px] text-muted-foreground border-t pt-2">
                Last run: <strong>{new Date(b2sConfig.last_run_at).toLocaleString()}</strong>
                {b2sConfig.last_run_summary && <> — {b2sConfig.last_run_summary}</>}
              </p>
            )}

            <p className="text-[11px] text-muted-foreground italic">
              Tip: this works alongside your existing RSS feed at <code>/blog.xml</code> — no separate hookup needed. The job reads <code>blog_posts</code> directly.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-[#00A896]" /> Social Media Generator
          </CardTitle>
          <CardDescription>
            Claude writes the caption + hashtags, Gemini Nano Banana generates a matching image. Auto-pushes to Notion if configured.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="sg-prompt">What's the post about?</Label>
            <Textarea
              id="sg-prompt"
              rows={3}
              placeholder='e.g. "Tip: When neurotypicals say I will think about it they usually mean no. Heres how to spot it."'
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              data-testid="sg-prompt-input"
            />
          </div>

          <div>
            <Label htmlFor="sg-image-prompt" className="text-sm">
              Image prompt <span className="text-muted-foreground font-normal text-xs">(optional — overrides what the AI draws; only used for static posts, not reels)</span>
            </Label>
            <Textarea
              id="sg-image-prompt"
              rows={2}
              placeholder='e.g. "Soft pastel illustration of a person at a coffee shop holding a thought-bubble that says I will think about it, sticky-note collage aesthetic"'
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              data-testid="sg-image-prompt-input"
            />
          </div>

          <div className="border rounded-md p-3 bg-muted/20">
            <RefImageLibrary selected={refImageIds} onChange={setRefImageIds} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Platform</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger data-testid="sg-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Format / Size</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger data-testid="sg-format-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FORMATS.map((f) => (<SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sg-tone">Tone (optional)</Label>
              <Input
                id="sg-tone"
                placeholder="playful, professional, sarcastic..."
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                data-testid="sg-tone-input"
              />
            </div>
          </div>

          {format === 'reel' && (
            <div className="border-l-4 border-[#00A896] pl-3 py-2 bg-[#00E5CC]/5 rounded space-y-3">
              <div>
                <Label className="text-sm">Reel scenes: {reelScenes}</Label>
                <div className="flex gap-2 mt-1">
                  {REEL_SCENES.map((n) => (
                    <Button
                      key={n}
                      size="sm"
                      variant={reelScenes === n ? 'default' : 'outline'}
                      onClick={() => setReelScenes(n)}
                      className={reelScenes === n ? 'bg-[#00E5CC] text-[#0d1f2d]' : ''}
                      data-testid={`reel-scenes-${n}`}
                    >
                      {n}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  ~{(reelScenes * 2.2).toFixed(1)}s reel. Each scene = one AI image with Ken Burns zoom + crossfade. Takes ~{reelScenes * 5}s to generate.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-sm">Background music</Label>
                  <Button
                    size="sm"
                    variant="ghost"
                    type="button"
                    onClick={() => audioUploadRef.current?.click()}
                    data-testid="reel-upload-audio-btn"
                  >
                    <Sparkles className="h-3 w-3 mr-1" /> Upload your own
                  </Button>
                  <input
                    ref={audioUploadRef}
                    type="file"
                    accept="audio/mpeg,audio/mp3,audio/wav,audio/aac,audio/x-m4a,audio/ogg"
                    className="hidden"
                    onChange={(e) => uploadAudio(e.target.files?.[0])}
                  />
                </div>
                <div className="grid grid-cols-1 gap-1 max-h-44 overflow-auto">
                  <label className="flex items-center gap-2 text-sm p-1.5 rounded hover:bg-white dark:hover:bg-slate-800 cursor-pointer">
                    <input
                      type="radio"
                      name="bgTrack"
                      checked={bgTrack === ''}
                      onChange={() => setBgTrack('')}
                      data-testid="bg-track-silent"
                    />
                    <span className="text-slate-700 dark:text-slate-200">No music (silent)</span>
                  </label>
                  {audioTracks.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-white dark:hover:bg-slate-800">
                      <label className="flex items-center gap-2 text-sm flex-1 cursor-pointer">
                        <input
                          type="radio"
                          name="bgTrack"
                          checked={bgTrack === t.id}
                          onChange={() => setBgTrack(t.id)}
                          data-testid={`bg-track-${t.id.replace(':', '-')}`}
                        />
                        <span className="text-slate-700 dark:text-slate-200">{t.label}</span>
                        {t.kind === 'custom' && <Badge variant="secondary" className="text-xs">custom</Badge>}
                      </label>
                      <audio
                        controls
                        src={`${API}/api/admin/reels/audio/preview/${encodeURIComponent(t.id)}`}
                        className="h-7 max-w-[180px]"
                        preload="none"
                      />
                      {t.kind === 'custom' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 h-7 w-7 p-0"
                          onClick={() => deleteCustomTrack(t.id)}
                          data-testid={`bg-track-delete-${t.id.replace(':', '-')}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                {bgTrack && (
                  <div className="mt-2">
                    <Label className="text-xs">Music volume: {(bgVolume * 100).toFixed(0)}%</Label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={bgVolume}
                      onChange={(e) => setBgVolume(parseFloat(e.target.value))}
                      className="w-full mt-1"
                      data-testid="bg-volume-slider"
                    />
                    <p className="text-xs text-slate-500">
                      Default 35% leaves room for voiceover later — auto-fades in/out 0.5s at edges.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeHashtags}
              onChange={(e) => setIncludeHashtags(e.target.checked)}
              data-testid="sg-include-hashtags"
            />
            Include hashtags
          </label>

          <Button
            onClick={generate}
            disabled={generating || prompt.trim().length < 8}
            className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00d4bd]"
            data-testid="sg-generate-btn"
          >
            {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {generating ? 'Generating (20-40s)...' : 'Generate post'}
          </Button>
        </CardContent>
      </Card>

      {/* Scheduled Queue — upcoming auto-publish posts. Renders only
          when at least one draft is queued. Lets the admin see what's
          on deck for the week + cancel/reschedule with one click. */}
      {scheduled.length > 0 && (
        <Card className="border-amber-500/40 bg-amber-500/5" data-testid="scheduled-queue-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4 text-amber-600" />
              Scheduled posts <Badge variant="secondary" className="text-[10px]">{scheduled.length}</Badge>
            </CardTitle>
            <CardDescription className="text-xs">
              Auto-publishes within 1 minute of the scheduled time. Meta + Threads via direct API; LinkedIn/TikTok/YouTube send you a reminder email with the asset + caption.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 pt-0">
            {scheduled.map((s) => (
              <div
                key={s.draft_id}
                className="flex items-center gap-3 p-2 rounded-md bg-card border"
                data-testid={`scheduled-item-${s.draft_id}`}
              >
                <img
                  src={`${API}${s.image_url}`}
                  alt=""
                  className="h-12 w-12 rounded object-cover shrink-0 border"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{s.caption?.slice(0, 70) || '(no caption)'}</p>
                  <div className="flex items-center gap-1 flex-wrap mt-0.5">
                    <span className="text-[11px] font-medium text-amber-700 dark:text-amber-300">
                      {new Date(s.scheduled_at).toLocaleString(undefined, {
                        weekday: 'short', month: 'short', day: 'numeric',
                        hour: 'numeric', minute: '2-digit',
                      })}
                    </span>
                    <span className="text-muted-foreground text-[11px]">·</span>
                    {(s.scheduled_platforms || []).map((p) => (
                      <Badge key={p} variant="outline" className="text-[9px] px-1 py-0">{p}</Badge>
                    ))}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => cancelSchedule(s.draft_id)}
                  className="h-7 text-xs text-red-600 hover:bg-red-500/10 shrink-0"
                  data-testid={`scheduled-cancel-${s.draft_id}`}
                  title="Cancel scheduled post"
                >
                  <XIcon className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <h3 className="text-sm font-medium text-slate-700">Recent drafts {drafts.length > 0 && <Badge variant="outline">{drafts.length}</Badge>}</h3>

      {loadingDrafts ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : drafts.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">
          <Share2 className="h-10 w-10 mx-auto mb-2 text-slate-300" />
          <p>No drafts yet. Generate your first one above.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {drafts.map((d) => (
            <Card key={d.draft_id} data-testid={`social-draft-${d.draft_id}`}>
              <CardContent className="p-4 flex gap-3">
                {d.is_reel && d.video_url ? (
                  <video
                    src={`${API}${d.video_url}`}
                    poster={`${API}${d.image_url}`}
                    controls
                    playsInline
                    className="w-24 h-40 object-cover rounded border flex-shrink-0 bg-black"
                    data-testid={`reel-video-${d.draft_id}`}
                  />
                ) : (
                  <img
                    src={`${API}${d.image_url}`}
                    alt={d.title}
                    className="w-24 h-24 object-cover rounded border flex-shrink-0"
                    data-testid={`social-img-${d.draft_id}`}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 flex-wrap mb-1">
                    {d.is_reel && (
                      <Badge className="bg-purple-100 text-purple-800 border-purple-300">
                        <Film className="h-3 w-3 mr-1" /> Reel
                      </Badge>
                    )}
                    <Badge variant="outline">{d.platform}</Badge>
                    <Badge variant="secondary" className="text-xs">{d.format_label}</Badge>
                    {d.duration_seconds && (
                      <Badge variant="outline" className="text-xs">{d.duration_seconds.toFixed(1)}s</Badge>
                    )}
                    {d.notion_url && (
                      <a href={d.notion_url} target="_blank" rel="noreferrer" className="text-xs text-[#00A896] flex items-center gap-1">
                        <ExternalLink className="h-3 w-3" /> Notion
                      </a>
                    )}
                  </div>
                  <p className="font-medium text-sm truncate">{d.title}</p>
                  <p className="text-xs text-slate-600 line-clamp-2 mt-1">{d.caption}</p>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPreviewDraft(d)}
                      data-testid={`social-preview-${d.draft_id}`}
                      title="Cross-post preview (FB / IG / Threads)"
                    >
                      <Eye className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => copyCaption(d.caption, d.hashtags)} data-testid={`social-copy-${d.draft_id}`}>
                      <Copy className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRewriteOpenDraft(d)}
                      className="text-[#00A896] hover:text-[#008b7a] hover:bg-[#00E5CC]/10 border-[#00E5CC]/30"
                      title="Rewrite this draft with AI — original stays safe until you Apply"
                      data-testid={`social-rewrite-with-ai-btn-${d.draft_id}`}
                    >
                      <Sparkles className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="outline" asChild>
                      <a href={`${API}${d.video_url || d.image_url}`} download data-testid={`social-download-${d.draft_id}`}>
                        <Download className="h-3 w-3" />
                      </a>
                    </Button>
                    {(metaConfig?.configured || metaConfig?.threads_configured) && !d.published_to && !d.is_reel && (
                      <Button
                        size="sm"
                        className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00d4bd] h-7"
                        onClick={() => publishToMeta(
                          d.draft_id,
                          metaConfig.configured,
                          !!metaConfig.ig_user_id,
                          !!metaConfig.threads_configured,
                        )}
                        disabled={publishingDraftId === d.draft_id}
                        data-testid={`social-publish-${d.draft_id}`}
                      >
                        {publishingDraftId === d.draft_id ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <Send className="h-3 w-3 mr-1" />
                        )}
                        Publish to {[
                          metaConfig.configured && 'FB',
                          metaConfig.ig_user_id && 'IG',
                          metaConfig.threads_configured && 'Threads',
                        ].filter(Boolean).join('+')}
                      </Button>
                    )}
                    {d.is_reel && metaConfig?.configured && (
                      <Badge variant="outline" className="h-7 px-2 flex items-center text-slate-600">
                        <Film className="h-3 w-3 mr-1" /> Download & upload manually to IG/TikTok
                      </Badge>
                    )}
                    {d.published_to && d.published_to.length > 0 && (
                      <Badge className="bg-emerald-100 text-emerald-800 h-7 px-2 flex items-center">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Posted to {d.published_to.join(', ')}
                      </Badge>
                    )}
                    {d.scheduled_at && !d.scheduled_handled && (
                      <Badge className="bg-amber-100 text-amber-800 h-7 px-2 flex items-center text-[10px]" title={new Date(d.scheduled_at).toLocaleString()}>
                        <Clock className="h-3 w-3 mr-1" /> Scheduled · {new Date(d.scheduled_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </Badge>
                    )}
                    {!d.scheduled_at && !d.published_to?.length && !d.is_reel && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openSchedule(d)}
                        className="h-7 text-xs border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
                        data-testid={`social-schedule-${d.draft_id}`}
                        title="Schedule this post for later"
                      >
                        <Clock className="h-3 w-3 mr-1" /> Schedule
                      </Button>
                    )}
                    {d.scheduled_at && !d.scheduled_handled && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => cancelSchedule(d.draft_id)}
                        className="h-7 text-xs text-red-600 hover:bg-red-500/10"
                        data-testid={`social-unschedule-${d.draft_id}`}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={() => deleteDraft(d.draft_id)} data-testid={`social-delete-${d.draft_id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CrossPostPreviewDialog
        draft={previewDraft}
        open={!!previewDraft}
        onClose={() => setPreviewDraft(null)}
      />

      {/* Schedule modal — datetime-local picker + platform checkboxes.
          Native datetime-local input renders the OS picker which is
          accessible + zero-dependency. Admin's local timezone is the
          source of truth; we convert to UTC before POSTing. */}
      {schedulingDraftId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !scheduleBusy && setSchedulingDraftId(null)}
          data-testid="schedule-modal-backdrop"
        >
          <div
            className="bg-card rounded-lg p-5 max-w-md w-full shadow-xl space-y-4 border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Calendar className="h-4 w-4 text-amber-600" /> Schedule post
              </h3>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSchedulingDraftId(null)}
                disabled={scheduleBusy}
                className="h-8 w-8 p-0"
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </div>

            <div>
              <label className="text-xs font-medium block mb-1">Date &amp; time (your local timezone)</label>
              <input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                className="w-full px-3 py-2 rounded-md border bg-background text-sm"
                min={new Date().toISOString().slice(0, 16)}
                data-testid="schedule-datetime-input"
              />
              {scheduleAt && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Publishes at <strong>{new Date(scheduleAt).toLocaleString()}</strong>
                </p>
              )}
              {/* Best-time-to-post auto-suggester — reads the checked
                  platforms below and computes the soonest peak slot.
                  Cheap UX win: one click instead of having to mentally
                  cross-reference each platform's optimal window. */}
              {(() => {
                const s = suggestBestTime(schedulePlatforms);
                if (!s) return null;
                return (
                  <button
                    type="button"
                    onClick={applySuggestedTime}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#00A896] hover:text-[#00876E] hover:underline cursor-pointer"
                    data-testid="schedule-best-time-suggest"
                  >
                    <Clock className="h-3 w-3" />
                    <span>🕒 {s.label} — click to apply</span>
                  </button>
                );
              })()}
            </div>

            <div>
              <label className="text-xs font-medium block mb-1.5">Publish to</label>
              <div className="grid grid-cols-2 gap-1.5">
                {['Facebook', 'Instagram', 'LinkedIn', 'TikTok', 'Threads', 'YouTube'].map((p) => {
                  const isAuto = ['Facebook', 'Instagram', 'Threads'].includes(p);
                  return (
                    <label
                      key={p}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md border cursor-pointer hover:bg-muted/40 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={schedulePlatforms[p]}
                        onChange={(e) => setSchedulePlatforms((s) => ({ ...s, [p]: e.target.checked }))}
                        className="accent-[#00A896]"
                        data-testid={`schedule-platform-${p.toLowerCase()}`}
                      />
                      <span className="flex-1">{p}</span>
                      <span className={`text-[9px] uppercase tracking-wide px-1 py-0.5 rounded ${isAuto ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-amber-500/15 text-amber-700 dark:text-amber-300'}`}>
                        {isAuto ? 'auto' : 'remind'}
                      </span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1.5">
                <span className="text-emerald-600 font-medium">auto</span> = published directly via API.
                <span className="text-amber-600 font-medium"> remind</span> = email with asset + caption (manual posting required).
              </p>
            </div>

            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button
                variant="outline"
                onClick={() => setSchedulingDraftId(null)}
                disabled={scheduleBusy}
                data-testid="schedule-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmSchedule}
                disabled={scheduleBusy || !scheduleAt}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="schedule-confirm-btn"
              >
                {scheduleBusy ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Saving…</>
                ) : (
                  <><Clock className="h-3.5 w-3.5 mr-1" /> Schedule</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* "Rewrite with AI" dialog for social drafts. Non-destructive —
          original stays in the DB until the admin clicks Apply inside
          the dialog (which PATCHes /drafts/{id}/apply-rewrite). */}
      <SocialRewriteDialog
        draft={rewriteOpenDraft}
        apiBase={API}
        open={!!rewriteOpenDraft}
        onOpenChange={(o) => { if (!o) setRewriteOpenDraft(null); }}
        onApplied={() => { loadDrafts(); }}
      />
    </div>
  );
};

export default SocialMediaGeneratorTab;
